import React from 'react';

import {
  List,
  Datagrid,
  TextField,
  DateField,
  usePermissions,
  SelectField,
  FunctionField,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';

import {
  Button, Card, Chip, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import actions, { Actions } from '../../constants/actions';

import hasAccess from '../../utilities/hasAccess';
import useFormStudioEnabled from '../../hooks/useFormStudioEnabled';

import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import LinkField from '../layout/LinkField';
import { languagesChoices } from '../../constants/languages';
import resourceNames from '../../constants/resourceNames';
import formStates, { FormStates } from '../../constants/formStates';
import FormStudioColumnLabel from './FormStudioColumnLabel';

type FormListRecord = {
  id: string;
  state?: FormStates;
  sourceFormId?: string;
};

const FormListActions = ({ onCreateDraft, disabled }: {
  onCreateDraft: () => void;
  disabled: boolean;
}) => (
  <TopToolbar>
    <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateDraft} disabled={disabled}>
      Create form
    </Button>
  </TopToolbar>
);

const FormList = () => {
  const studioEnabled = useFormStudioEnabled();
  const { permissions } = usePermissions<Actions[]>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const canCreate = hasAccess(permissions, actions.FORM_CREATE)
    && hasAccess(permissions, actions.FORM_UPDATE);
  const canCreateFromPublished = canCreate && hasAccess(permissions, actions.FORM_GET);
  const canUpdate = hasAccess(permissions, actions.FORM_UPDATE);

  const createDraft = async (sourceFormId?: string): Promise<void> => {
    try {
      const resource = sourceFormId
        ? `${resourceNames.FORMS}/${sourceFormId}/drafts`
        : `${resourceNames.FORMS}/drafts`;
      const { data } = await dataProvider.create<FormListRecord>(resource, { data: {} });
      refresh();
      navigate(`/${resourceNames.FORMS}/${data.id}/studio`);
    } catch {
      notify('The form draft could not be created.', { type: 'error' });
    }
  };

  return (
    <List
      empty={false}
      filter={studioEnabled ? { includeDrafts: true } : undefined}
      actions={studioEnabled
        ? <FormListActions onCreateDraft={() => createDraft()} disabled={!canCreate} />
        : undefined}
      pagination={<Pagination disableEmptyText />}
      exporter={false}
      perPage={50}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty={<NoResults variant="h6" />}>
          {hasAccess(permissions, actions.FORM_GET) && <LinkField sortable={false} source="id" label="ID" />}
          {!hasAccess(permissions, actions.FORM_GET) && <TextField sortable={false} source="id" label="ID" />}
          <TextField sortable={false} source="title" />
          {studioEnabled && (
            <FunctionField<FormListRecord>
              label="State"
              render={(record) => (
                <Chip
                  size="small"
                  variant="outlined"
                  color={record.state === formStates.DRAFT ? 'warning' : 'success'}
                  label={record.state === formStates.DRAFT ? 'Draft' : 'Published'}
                />
              )}
            />
          )}
          {studioEnabled && (
            <FunctionField<FormListRecord>
              label="Draft source"
              render={(record) => (record.state === formStates.DRAFT ? record.sourceFormId || 'New form' : '')}
            />
          )}
          {studioEnabled && (
            <FunctionField<FormListRecord>
              label={<FormStudioColumnLabel />}
              sortable={false}
              render={(record) => (
                <Stack direction="row" spacing={1}>
                  {record.state === formStates.DRAFT && canUpdate ? (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/${resourceNames.FORMS}/${record.id}/studio`);
                      }}
                    >
                      Open Studio
                    </Button>
                  ) : null}
                  {record.state !== formStates.DRAFT && canCreateFromPublished && (
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        createDraft(record.id);
                      }}
                    >
                      Create draft
                    </Button>
                  )}
                </Stack>
              )}
            />
          )}
          <SelectField sortable={false} source="language" choices={languagesChoices} />
          <DateField sortable source="createdAt" showTime />
        </Datagrid>
      </Card>
    </List>
  );
};

export default FormList;
